<template>
  <base-badge
    v-if="user !== null"
    :right="right"
  >
    <template #avatar>
      <person-avatar :person="user" />
    </template>
    <template #first-line>
      <v-row
        no-gutters
        class="primary-text-color font-weight-bold main-name align-center flex-nowrap"
        :class="{reversed: right, 'justify-end': right}"
      >
        <v-col
          class="shrink"
          :data-test="AutomationTestsIds.userManagement.userBadge.userName"
        >
          {{ user.displayName }}<span
            v-if="organisationName"
            class="font-italic font-weight-light ml-2"
          >({{ organisationName }})</span><span
            v-if="showLanguageFlags && spokenLanguageFlags.length"
            class="mx-2 font-weight-regular"
          >{{ spokenLanguageFlags.join(' ') }}</span>
        </v-col>
        <v-col>
          <slot />
        </v-col>
      </v-row>
    </template>
    <template
      v-if="user"
      #second-line
    >
      <span
        v-if="showLogin && !$scopedSlots['second-line']"
        class="text-core-onCore-lowEmphasis!"
      >
        {{ user.username }}
      </span>
      <slot name="second-line" />
    </template>
    <template
      v-if="user"
      #suffix
    >
      <slot name="suffix" />
    </template>
  </base-badge>
  <v-skeleton-loader
    v-else
    class="transparent"
    type="list-item-avatar"
  />
</template>

<script setup lang="ts">
import {computed} from 'vue';
import User from '@/models/user/User';
import PersonAvatar from '@/components/commons/PersonAvatar.vue';
import BaseBadge from '@/components/badges/base/BaseBadge.vue';
import {LanguageCountryCodes, Languages} from '@/commons/enums/Languages';
import {useCountryFlag} from '@/composables/useCountryFlags';
import {useInstance} from '@/composables/useInstance';
import {AutomationTestsIds} from '@/env/AutomationTests/ids/AutomationTestsIds';

const props = withDefaults(defineProps<{
  user?: Nullable<User>;
  showId?: boolean;
  showLogin?: boolean;
  right?: boolean;
  showLanguageFlags?: boolean;
}>(), {
  user: null,
  showId: false,
  showLogin: false,
  right: false,
  showLanguageFlags: false,
});

const {getFlagEmoji} = useCountryFlag();
const {$i18n} = useInstance();
const t = $i18n.OrganisationId;

const organisationName = computed<Nullable<string>>(() =>
  props.user?.organisationId ? t[props.user.organisationId] : null
);

const spokenLanguageFlags = computed(() => {
  const user = props.user;
  if (!props.showLanguageFlags || !user) {
    return [];
  }

  const canSpeak: Record<Languages, boolean | undefined> = {
    [Languages.ENGLISH]: user.canSpeakEnglish ?? user.privileges?.canSpeak?.[Languages.ENGLISH],
    [Languages.GERMAN]: user.canSpeakGerman ?? user.privileges?.canSpeak?.[Languages.GERMAN],
    [Languages.UKRAINIAN]: user.canSpeakUkrainian ?? user.privileges?.canSpeak?.[Languages.UKRAINIAN],
  };

  return (Object.values(Languages) as Languages[])
    .filter(language => canSpeak[language])
    .map(language => getFlagEmoji(LanguageCountryCodes[language]));
});
</script>

<style scoped lang="scss">
  .user-badge ::v-deep .v-list-item--avatar{
    padding-right: 0 !important;
    padding-left: 0 !important;;
  }
  .reversed-badge{
    .avatar{
      display: flex;
    }

    ::v-deep .v-list-item{
      flex-direction: row-reverse;
      padding-left: 0;
    }
  }

  .main-client {
    .client-display * {
      font-size: 16px;
    }
  }

  ::v-deep .v-list-item__title{
    line-height: 24px;
    height: 24px;
  }
  ::v-deep .v-list-item__subtitle {
    line-height: 20px;
    height: 20px;
  }

  ::v-deep .v-skeleton-loader__list-item-avatar {
    background-color: transparent !important;
    padding-left: 0px;
  }
</style>
